import { Check, Minus, Plus, Users } from "lucide-react";
import { inr } from "@/lib/format";

const Availability = ({ n, loading }) => {
  if (loading) return <span className="text-xs text-[#6b6b6b]">Checking…</span>;
  if (n === undefined) return null;
  if (n === 0) return <span className="text-xs text-red-700 font-medium">Sold out for these dates</span>;
  return <span className={`text-xs font-medium ${n <= 2 ? "text-[#C28B5E]" : "text-[#1E3B2E]"}`}>{n} available</span>;
};

const Picker = ({ slug, value, max, onChange, unknown }) => {
  if (value === 0) {
    return (
      <button type="button" disabled={unknown || max < 1} onClick={() => onChange(1)} className="btn-primary w-full sm:w-44 !h-11" data-testid={`room-select-btn-${slug}`}>
        {unknown ? "Select dates" : max < 1 ? "Sold out" : "Select room"}
      </button>
    );
  }
  return (
    <div className="flex items-center justify-between w-full sm:w-44 h-11 border-2 border-[#1E3B2E] bg-white">
      <button type="button" aria-label="Remove room" onClick={() => onChange(value - 1)} className="h-full w-12 flex items-center justify-center text-[#1E3B2E]" data-testid={`room-qty-minus-${slug}`}><Minus size={16} /></button>
      <span className="text-sm font-semibold text-[#1E3B2E]" data-testid={`room-qty-${slug}`}>{value} room{value > 1 ? "s" : ""}</span>
      <button type="button" aria-label="Add room" disabled={value >= max} onClick={() => onChange(value + 1)} className="h-full w-12 flex items-center justify-center text-[#1E3B2E] disabled:opacity-30" data-testid={`room-qty-plus-${slug}`}><Plus size={16} /></button>
    </div>
  );
};

export default function RoomSelector({ rooms, qty, setQty, available, loading }) {
  return (
    <div className="space-y-3" data-testid="room-selector">
      {rooms.map((r) => {
        const avail = available?.[r.slug];
        const n = qty[r.slug] || 0;
        return (
          <div key={r.slug} data-testid={`room-row-${r.slug}`}
            className={`relative bg-white border-2 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-5 sm:items-center transition-colors ${n ? "border-[#1E3B2E] bg-[#F4EFE6]/40" : "border-[#E2DCD0]"}`}>
            {n > 0 && <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#1E3B2E] text-white flex items-center justify-center"><Check size={14} /></span>}
            <div className="flex gap-3 sm:gap-4 flex-1 min-w-0">
              {r.image && <img src={r.image} alt={r.name} loading="lazy" className="h-20 w-24 sm:h-24 sm:w-32 object-cover shrink-0" />}
              <div className="min-w-0 pr-6 sm:pr-0">
                <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-bold text-[#1E3B2E] leading-tight">{r.name}</p>
                <p className="text-xs text-[#6b6b6b] mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Users size={11} />Up to {r.capacity}</span>
                  <Availability n={avail} loading={loading} />
                </p>
                <p className="mt-1">{r.price ? <><span className="font-['Cormorant_Garamond'] text-2xl font-bold">{inr(r.price)}</span> <span className="text-xs text-[#6b6b6b]">/ night</span></> : <span className="text-sm font-medium">Price on request</span>}</p>
              </div>
            </div>
            <Picker slug={r.slug} value={n} max={avail ?? 0} unknown={loading || avail === undefined} onChange={(v) => setQty((q) => ({ ...q, [r.slug]: v }))} />
          </div>
        );
      })}
      <p className="text-xs text-[#6b6b6b]">All prices include GST &amp; breakfast. Extra bed complimentary.</p>
    </div>
  );
}
